"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.tryCatch = exports.validateFieldsToUpdate = exports.useConditionalPromise = exports.promiseAny = exports.promiseAllSettled = exports.parallelize = exports.isInEnum = exports.getClassAttributes = exports.extractValuesFromEnumArray = exports.absoluteValue = void 0;
const lodash_1 = require("lodash");
const consts_1 = require("../types/consts");
const enums_1 = require("../types/enums");
const array_util_1 = require("./array.util");
/**
 * Calculates the absolute value of a number.
 *
 * @param value - The number to calculate the absolute value of.
 * @returns The absolute value of the input number.
 */
function absoluteValue(value) {
    return Math.abs(value ?? 0);
}
exports.absoluteValue = absoluteValue;
/**
 * Executes multiple promises and returns an array of settled results.
 *
 * @param promises - The promises to execute.
 * @returns A promise that resolves to an array of fulfilled values.
 */
async function promiseAllSettled(...promises) {
    if (!promises?.length) {
        return [];
    }
    const result = await Promise.allSettled(promises);
    const failed = result.filter((promise) => {
        return promise.status === enums_1.PromiseStatusEnum.REJECTED;
    });
    if (failed.length) {
        const internalError = failed.find(promise => {
            return promise.reason?.status >= enums_1.HttpStatus.INTERNAL_SERVER_ERROR;
        });
        const message = (0, array_util_1.uniqueItems)(failed.map(error => error.reason.message ?? error.reason)).join('\n');
        const thrownError = internalError ? internalError?.reason : failed[0]?.reason;
        if (thrownError instanceof Error) {
            thrownError.message = message;
            throw thrownError;
        }
        else {
            throw new Error(message);
        }
    }
    return result.map((promise) => promise.value);
}
exports.promiseAllSettled = promiseAllSettled;
/**
 * This function fulfills as soon as one of the promises in the iterable fulfills, with the value of the first promise that fulfills.
 * ECMA2020 introduced Promise.any() which is a built-in method that does the same thing.
 *
 * @param promises - An iterable of unawaited promises to watch.
 * @returns A promise that resolves with the value of the first promise to be fulfilled or rejects with an array of rejection reasons if the promises rejected.
 */
function promiseAny(promises) {
    return new Promise((resolve, reject) => {
        if (promises.length === 0) {
            // Reject with an error when the iterable is empty
            reject(new Error("No promises provided"));
            return;
        }
        let rejections = 0;
        const errors = [];
        promises.forEach(promise => {
            promise.then(resolve).catch(error => {
                errors.push(error);
                rejections++;
                if (rejections === promises.length) {
                    reject(errors);
                }
            });
        });
    });
}
exports.promiseAny = promiseAny;
/**
 * Checks if a value is a valid member of an enum.
 *
 * @param enumType - The enum to check against.
 * @param value - The value to check.
 * @returns True if the value is a valid member of the enum, false otherwise.
 */
function isInEnum(enumType, value) {
    return Object.values(enumType).includes(value);
}
exports.isInEnum = isInEnum;
/**
 * Extracts values from an array of enums.
 *
 * @param values The array of values to extract from.
 * @param enumType The enum type to filter the values against.
 * @returns An array of values that are present in the enum.
 */
function extractValuesFromEnumArray(values, enumType) {
    return values?.filter(value => isInEnum(enumType, value));
}
exports.extractValuesFromEnumArray = extractValuesFromEnumArray;
/**
 * Executes a promise-based operation conditionally based on a boolean condition.
 *
 * @param operation - The promise-based operation to be executed.
 * @param condition - The boolean condition that determines whether to execute the operation or use the fallback.
 * @param fallback - An optional fallback function to be executed if the condition is false. Its result will be returned as a promise.
 * @returns A promise that resolves to the result of the operation or the fallback function.
 */
function useConditionalPromise(operation, condition, fallback) {
    return condition ? operation() : Promise.resolve(fallback?.());
}
exports.useConditionalPromise = useConditionalPromise;
/**
 * Validates the fields to update in an entity object.
 *
 * @template T - The type of the entity object.
 * @param {new () => T} classType - The constructor function of the entity object.
 * @param {T} entity - The original entity object.
 * @param {Partial<T>} fieldsToUpdate - The fields to update in the entity object.
 * @returns {boolean} - Returns true if there are fields to update, false otherwise.
 */
function validateFieldsToUpdate(classType, entity, fieldsToUpdate) {
    return !getClassAttributes(classType).every((field) => !fieldsToUpdate[field] || fieldsToUpdate[field] === entity[field]);
}
exports.validateFieldsToUpdate = validateFieldsToUpdate;
/**
 * Retrieves the attributes of a class instance.
 *
 * @template T - The type of the class.
 * @param classType - The constructor function of the class.
 * @returns An array of attribute names.
 */
function getClassAttributes(classType) {
    return Object.keys(new classType());
}
exports.getClassAttributes = getClassAttributes;
/**
 * Executes the given async function in parallel for each item in the array.
 * Returns a promise that resolves to an array of results.
 *
 * @param array - The array of items to process.
 * @param func - The async function to execute for each item.
 * @param chunkSize - The number of items to process in each chunk (default: DEFAULT_CHUNK_SIZE).
 * @returns A promise that resolves to an array of results.
 */
async function parallelize(array, func, chunkSize = consts_1.DEFAULT_CHUNK_SIZE) {
    const results = [];
    for (const part of (0, lodash_1.chunk)(array, chunkSize)) {
        results.push(...(await promiseAllSettled(...part.map(part => func(part)))));
    }
    return results;
}
exports.parallelize = parallelize;
/**
 * Wraps a synchronise function in a try-catch-finally block and calls the onError and onFinally respectably.
 * @template T - The return type of the synchronised function
 * @param fn {() => T} - The function to execute
 * @param onError {(error: Error) => void} - (Optional) The function to call if an error is thrown.
 * @param onFinally {() => void} - (Optional) The function to call after the try-catch block
 */
function tryCatch(fn, onError, onFinally) {
    try {
        return fn();
    }
    catch (error) {
        onError?.(error);
    }
    finally {
        onFinally?.();
    }
}
exports.tryCatch = tryCatch;
//# sourceMappingURL=general.util.js.map